import React from "react";
import {
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Box,
  MenuItem,
} from "@mui/material";
import {
  Dcdc,
  DcdcAdjustable,
  LabPowerSupply,
  Linear,
  PowerSupply,
} from "../../models/signalSources/power_supply.ts";
import { Signal } from "../../models/signal.ts";

interface Props {
  data: PowerSupply;
  onChange: (value: PowerSupply) => void;
}

const PowerSupplyForm: React.FC<Props> = ({
  data,
  onChange,
}) => {
  const linearMinVolt = 1.8;
  const linearMaxVolt = 8.0;
  const adjustableDcdcMinVolt = 9;
  const adjustableDcdcMaxVolt = 30;

  const getLinearNetName = (number: number, volt: number) => {
    return `${volt.toFixed(2)}V [L${number}]`;
  }

  const getAdjustableDcdcNetName = (number: number, volt: number) => {
    return `${volt.toFixed(2)}V [DCDC${number}]`;
  }

  const updateLabPowerSupply = (labPowerSupply: LabPowerSupply) => {
    onChange({
      ...data,
      labPowerSupply,
    });
  };

  const handleLabEnableChange = (enabled: boolean) => {
    updateLabPowerSupply({ ...data.labPowerSupply, enable: enabled });
  };

  const handleLabChannelsChange = (channels: 1 | 2 | 3) => {
    updateLabPowerSupply({ ...data.labPowerSupply, channels });
  };

  const handleLabSignalChange = (
    channelNumber: 1 | 2 | 3,
    field: keyof Pick<Signal, "name">,
    value: string,
  ) => {
    const channelKey = `channel${channelNumber}` as keyof Pick<
      LabPowerSupply,
      "channel1" | "channel2" | "channel3"
    >;
    const channel = data.labPowerSupply[channelKey];
    updateLabPowerSupply({
      ...data.labPowerSupply,
      [channelKey]: {
        ...channel,
        positive: {
          ...channel.positive,
          [field]: value,
          net: `Ext. Power Supply Channel ${channelNumber} VCC`,
          source_type: "external_supply",
          source_id: channel.positive.source_id ?? channel.positive.id,
        },
      },
    });
  };

  const handleLinearEnableChange = (number: number, enabled: boolean) => {
    const key = `linear${number}` as keyof PowerSupply;
    const updatedLinear = { ...data[key] as Linear, enable: enabled };
    const updatedData: PowerSupply = {
      ...data,
      [key]: updatedLinear,
    };
    onChange(updatedData);
  };

  const handleLinearVoltChange = (number: number, volt: number) => {
    if (volt < linearMinVolt || volt > linearMaxVolt) return;
    const key = `linear${number}` as keyof PowerSupply;
    let updatedLinear = { ...data[key] as Linear, volt: volt };
    updatedLinear = { ...data[key] as Linear, net: getLinearNetName(number, volt), volt: volt };
    const updatedData: PowerSupply = {
      ...data,
      [key]: updatedLinear,
    };
    onChange(updatedData);
  };

  const handleAdjustableDcdcEnableChange = (number: number, enabled: boolean) => {
    const key = `dcdcAdjustable${number}` as keyof PowerSupply;
    const updatedDcdc = { ...data[key] as DcdcAdjustable, enable: enabled };
    const updatedData: PowerSupply = {
      ...data,
      [key]: updatedDcdc,
    };
    onChange(updatedData);
  };

  const handleAdjustableDcdcVoltChange = (number: number, volt: number) => {
    if (volt < adjustableDcdcMinVolt || volt > adjustableDcdcMaxVolt) return;
    const key = `dcdcAdjustable${number}` as keyof PowerSupply;
    let updatedDcdc = { ...data[key] as DcdcAdjustable, volt: volt };
    updatedDcdc = { ...data[key] as Linear, net: getAdjustableDcdcNetName(number, volt), volt: volt };
    const updatedData: PowerSupply = {
      ...data,
      [key]: updatedDcdc,
    };
    onChange(updatedData);
  };

  const handleDcdcEnableChange = (key: keyof PowerSupply, enabled: boolean) => {
    const updatedDcdc = {
      ...data[key] as Dcdc,
      enable: enabled,
    };
    const updatedData: PowerSupply = {
      ...data,
      [key]: updatedDcdc,
    };
    onChange(updatedData);
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Feste Spannungsquellen
      </Typography>
      <Box mt={2} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.dcdc24V.enable}
                onChange={(e) => handleDcdcEnableChange("dcdc24V", e.target.checked)}
              />
            }
            label={data.dcdc24V.label}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.dcdc12V.enable}
                onChange={(e) => handleDcdcEnableChange("dcdc12V", e.target.checked)}
              />
            }
            label={data.dcdc12V.label}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.dcdc5V.enable}
                onChange={(e) => handleDcdcEnableChange("dcdc5V", e.target.checked)}
              />
            }
            label={data.dcdc5V.label}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.dcdc3V3.enable}
                onChange={(e) => handleDcdcEnableChange("dcdc3V3", e.target.checked)}
              />
            }
            label={data.dcdc3V3.label}
          />
        </Grid>
      </Grid>
      <Box mt={2} />
      <Typography variant="h6" gutterBottom>
        Einstellbare Linearspannungsquellen
      </Typography>
      <Box mt={2} />
      <Grid container spacing={2}>
        {[1, 2, 3].map((number) => {
          const linearKey = `linear${number}` as keyof PowerSupply;
          const linear = data[linearKey] as Linear;

          return (
            <React.Fragment key={number}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={linear?.enable || false}
                      onChange={(e) =>
                        handleLinearEnableChange(number, e.target.checked)
                      }
                    />
                  }
                  label={linear.label}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  type="number"
                  label={`Spannung (V)`}
                  value={linear?.volt ?? ""}
                  disabled={!linear?.enable}
                  inputProps={{
                    step: 0.01,
                    min: linearMinVolt,
                    max: linearMaxVolt,
                  }}
                  onChange={(e) =>
                    handleLinearVoltChange(number, parseFloat(e.target.value))
                  }
                  fullWidth
                />
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>
      <Typography variant="h6" gutterBottom>
        Einstellbare Step-Up und Step-Down Quellen
      </Typography>
      <Box mt={2} />
      <Grid container spacing={2}>
        {[1, 2, 3].map((number) => {
          const dcdcKey = `dcdcAdjustable${number}` as keyof PowerSupply;
          const dcdc = data[dcdcKey] as DcdcAdjustable;

          return (
            <React.Fragment key={number}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dcdc?.enable || false}
                      onChange={(e) =>
                        handleAdjustableDcdcEnableChange(number, e.target.checked)
                      }
                    />
                  }
                  label={dcdc.label}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  type="number"
                  label={`Spannung (V)`}
                  value={dcdc?.volt ?? ""}
                  disabled={!dcdc?.enable}
                  inputProps={{
                    step: 0.1,
                    min: adjustableDcdcMinVolt,
                    max: adjustableDcdcMaxVolt,
                  }}
                  onChange={(e) =>
                    handleAdjustableDcdcVoltChange(number, parseFloat(e.target.value))
                  }
                  fullWidth
                />
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>
      <Box mt={2} />
      <Typography variant="h6" gutterBottom>
        Externes Labornetzteil
      </Typography>
      <Box mt={2} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.labPowerSupply.enable}
                onChange={(e) => handleLabEnableChange(e.target.checked)}
              />
            }
            label={data.labPowerSupply.label}
          />
        </Grid>
        {data.labPowerSupply.enable && (
          <>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label="Kanäle"
            value={data.labPowerSupply.channels}
            onChange={(event) =>
              handleLabChannelsChange(Number(event.target.value) as 1 | 2 | 3)
            }
            fullWidth
          >
            <MenuItem value={1}>1 Kanal</MenuItem>
            <MenuItem value={2}>2 Kanäle</MenuItem>
            <MenuItem value={3}>3 Kanäle</MenuItem>
          </TextField>
        </Grid>
        {([1, 2, 3] as const)
          .filter((channelNumber) => channelNumber <= data.labPowerSupply.channels)
          .map((channelNumber) => {
            const channelKey = `channel${channelNumber}` as keyof Pick<
              LabPowerSupply,
              "channel1" | "channel2" | "channel3"
            >;
            const channel = data.labPowerSupply[channelKey];
            return (
              <React.Fragment key={channelNumber}>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      sx={{ display: "flex", alignItems: "center", minHeight: 56 }}
                    >
                      <Typography>Kanal {channelNumber}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Name"
                        value={channel.positive.name}
                    onChange={(event) =>
                      handleLabSignalChange(channelNumber, "name", event.target.value)
                    }
                    fullWidth
                  />
                </Grid>
              </React.Fragment>
            );
          })}
          </>
        )}
      </Grid>
    </>
  );
};

export default PowerSupplyForm;
